// Per-protocol identity catalog — what to label and which fields to show for each
// key that can appear under `protocols{}` on the asset document.
//
// `protocols{}` is an OPEN set: any OT/IoMT parser can introduce a new key with no
// whitelist on the wire (verified against the probe's source — see
// `asset-classification-task/docs/architecture/ORION_FINAL_FIELDS_CATALOG.md` §4).
// This file is therefore deliberately a *lookup*, not a fixed list: a protocol key
// this catalog doesn't know yet still renders, via the fallback in
// `ProtocolIdentitySection.jsx` — it just gets its raw field names title-cased
// instead of a curated label. Extending coverage for a new protocol is: add one
// entry here, nothing else.
//
// Covers BOTH what a device declares about itself over an OT/IoMT protocol
// (vendor/model/serial/firmware — Modbus, S7, BACnet, DICOM, ONVIF, SNMP
// ENTITY-MIB, ...) AND the raw banners Zeek-native protocols expose (NTLM,
// SMB, SSH, HTTP, FTP, Telnet, MySQL) — the latter used to be left out on the
// theory that they were "shown elsewhere", but none of them actually has a
// dedicated section anywhere in the dashboard, so omitting them just hid real
// data (an asset reporting 6 protocols showed only 2 cards). Only `snmp` and
// `dhcp` are genuinely shown elsewhere (their own sections, with confidence),
// so those two stay excluded — see `DUPLICATED_ELSEWHERE` in
// `ProtocolIdentitySection.jsx`.
//
// Each field is `[key, mono]` — `mono` decides monospace rendering (codes,
// versions, hex) vs plain text (declared names/free text); kept per-field per
// protocol rather than one global set, since the same key name can mean a
// short code in one protocol and free text in another.
export const PROTOCOL_IDENTITY_CATALOG = {
  modbus:   { label: "Modbus",              fields: [["vendor", false], ["product_code", true], ["revision", true], ["product_name", false], ["device_name", false]] },
  s7:       { label: "Siemens S7",          fields: [["vendor", false], ["model", true], ["product_code", true], ["hardware_revision", true], ["firmware", true], ["device_name", false], ["product_name", false], ["serial", true], ["serial_secondary", true]] },
  bacnet:   { label: "BACnet",              fields: [["vendor", false], ["model", true], ["software_version", true], ["firmware", true], ["device_name", false], ["serial", true], ["serial_secondary", true]] },
  profinet: { label: "PROFINET",            fields: [["vendor", false], ["model", true], ["product_code", true], ["device_name", false], ["declared_type", false], ["serial_secondary", true]] },
  enip:     { label: "EtherNet/IP",         fields: [["vendor", false], ["product_code", true], ["declared_type", false], ["product_name", false], ["revision", true], ["serial", true], ["serial_secondary", true]] },
  opcua:    { label: "OPC UA",              fields: [["vendor", false], ["product_name", false], ["product_code", true], ["software_version", true], ["revision", true], ["app_id", true]] },
  "iec-mms":{ label: "IEC 61850 MMS",       fields: [["vendor", false], ["model", true], ["revision", true]] },
  hl7:      { label: "HL7",                 fields: [["app_id", true], ["facility_name", false]] },
  dicom:    { label: "DICOM",               fields: [["vendor", false], ["model", true], ["modality", false], ["serial", true], ["serial_secondary", true], ["software_version", true], ["device_name", false]] },
  onvif:    { label: "ONVIF (camera)",      fields: [["vendor", false], ["model", true], ["declared_type", false]] },
  mdpws:    { label: "MDPWS (medical WS-Discovery)", fields: [["serial_secondary", true]] },
  snmp_entity_mib: { label: "SNMP ENTITY-MIB", fields: [["vendor", false], ["model", true], ["software_version", true], ["firmware", true], ["hardware_revision", true], ["serial", true], ["device_name", false]] },
  ipp:      { label: "Printer (IPP)",       fields: [["vendor", false], ["product_name", false], ["firmware", true], ["declared_type", false]] },
  fhir:     { label: "FHIR",                fields: [["app_id", true]] },
  tcp_fingerprint: { label: "TCP Fingerprint", fields: [["vendor", false]] },
  mdns_txt: { label: "mDNS (Apple)",        fields: [["vendor", false], ["model", true]] },

  // ── Raw banners — declared, not inferred, but not a curated "identity slot" ──
  ntlm:     { label: "NTLM",   fields: [["workstation", false], ["domain", false], ["server_nb_computer_name", false], ["server_dns_computer_name", false], ["version_major", true], ["version_minor", true], ["version_build", true]] },
  smb:      { label: "SMB",    fields: [["native_os", false], ["native_lanman", false], ["dialect", true], ["dialect_hex", true]] },
  ssh:      { label: "SSH",    fields: [["banner", false], ["version_raw", true]] },
  http:     { label: "HTTP",   fields: [["server", false], ["user_agent", false]] },
  ftp:      { label: "FTP",    fields: [["banner", false]] },
  telnet:   { label: "Telnet", fields: [["banner", false]] },
  mysql:    { label: "MySQL",  fields: [["server_version", true]] },
};

// Generic field-name → label, shared by every protocol above and by the fallback
// renderer for a protocol key this catalog doesn't recognise yet.
export const IDENTITY_FIELD_LABELS = {
  vendor: "Vendor",
  model: "Model",
  product_code: "Product Code",
  product_name: "Product Name",
  declared_type: "Declared Type",
  device_name: "Device Name",
  firmware: "Firmware",
  software_version: "Software Version",
  revision: "Revision",
  hardware_revision: "Hardware Revision",
  serial: "Serial",
  serial_secondary: "Serial (secondary)",
  modality: "Modality",
  app_id: "App ID",
  facility_name: "Facility Name",
  workstation: "Workstation",
  domain: "Domain",
  server_nb_computer_name: "NetBIOS Name",
  server_dns_computer_name: "DNS Name",
  version_major: "Version (major)",
  version_minor: "Version (minor)",
  version_build: "Version (build)",
  native_os: "Native OS",
  native_lanman: "Native LAN Manager",
  dialect: "Dialect",
  dialect_hex: "Dialect (hex)",
  banner: "Banner",
  version_raw: "Version (raw)",
  server: "Server",
  user_agent: "User-Agent",
  server_version: "Server Version",
};

const titleCase = (key) =>
  String(key ?? "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const identityFieldLabel = (key) => IDENTITY_FIELD_LABELS[key] ?? titleCase(key);

export const protocolLabel = (key) => PROTOCOL_IDENTITY_CATALOG[key]?.label ?? titleCase(key);

// Bookkeeping carried on every protocol block alongside whatever identity it
// declares (when it declares any) — never a claim about the device itself, so
// the fallback for a protocol this catalog doesn't know yet must not show them.
// A curated entry above never lists these, so this only matters for fallback.
const NON_IDENTITY_KEYS = new Set([
  "first_seen", "last_seen", "ts", "timestamp", "priority", "source", "confidence",
  "event_count", "count", "type", "transport", "port",
]);

// A protocol this catalog has no entry for still renders — via every key its own
// block actually carries — but capped, so one verbose/unexpected protocol block
// can't blow up a card the way an unbounded object dump would.
const FALLBACK_FIELD_LIMIT = 8;

// [key, mono] pairs to render for a protocol block: the curated list if known
// (already hand-picked, short, per-field mono flag), otherwise every
// non-bookkeeping key the object actually carries — capped, and guessed mono
// only for the generic patterns (version/hex/code-looking key names) — so an
// unknown future protocol is never silently dropped but also never renders
// unbounded or mis-styled.
export const identityFieldsFor = (protoKey, block) => {
  const curated = PROTOCOL_IDENTITY_CATALOG[protoKey]?.fields;
  if (curated) return curated;
  return Object.keys(block || {})
    .filter((k) => !NON_IDENTITY_KEYS.has(k))
    .slice(0, FALLBACK_FIELD_LIMIT)
    .map((k) => [k, /version|revision|code|hex|dialect|serial|id$/i.test(k)]);
};
